import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiArrowRight, FiSearch, FiX } from "react-icons/fi";
import { navItems, quickLinks } from "../data/hrmMockData";

const normalizeSearchText = (value) => String(value || "").trim().toLowerCase();

const HrmCommandPalette = ({
  canViewEvents = false,
  canViewMyCases = false,
  formsAvailable = false,
  formsActionRequired = false,
  onAction,
  onClose,
  onNavigate,
  open = false,
}) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const resultRefs = useRef([]);

  const destinations = useMemo(
    () => navItems.filter((item) => {
      if (item.id === "myForms") {
        return formsAvailable || formsActionRequired;
      }
      if (item.id === "events") {
        return canViewEvents;
      }
      return !item.workerOnly || canViewMyCases;
    }),
    [canViewEvents, canViewMyCases, formsActionRequired, formsAvailable]
  );

  const normalizedQuery = normalizeSearchText(query);
  const filteredDestinations = destinations.filter((item) =>
    normalizeSearchText(item.label).includes(normalizedQuery)
  );
  const filteredActions = quickLinks.filter((item) =>
    normalizeSearchText(item.label).includes(normalizedQuery)
  );
  const resultCount = filteredDestinations.length + filteredActions.length;

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(-1);
      resultRefs.current = [];
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    window.requestAnimationFrame(() => inputRef.current?.focus());

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveIndex((currentIndex) => {
      if (!resultCount) {
        return -1;
      }
      return currentIndex >= 0 && currentIndex < resultCount ? currentIndex : 0;
    });
  }, [open, resultCount]);

  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }
    resultRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  const selectDestination = (itemId) => {
    onClose();
    onNavigate(itemId);
  };

  const selectAction = (item) => {
    onClose();
    if (item.action === "profile" || item.action === "directory") {
      onNavigate(item.action);
      return;
    }
    onAction(item.action);
  };

  const selectResult = (resultIndex) => {
    if (resultIndex < filteredDestinations.length) {
      if (filteredDestinations[resultIndex].href) {
        resultRefs.current[resultIndex]?.click();
        return;
      }
      selectDestination(filteredDestinations[resultIndex].id);
      return;
    }

    const action = filteredActions[resultIndex - filteredDestinations.length];
    if (action) {
      selectAction(action);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (!resultCount || !["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
      return;
    }

    event.preventDefault();
    if (event.key === "Enter") {
      selectResult(activeIndex >= 0 ? activeIndex : 0);
      return;
    }

    setActiveIndex((currentIndex) => {
      if (event.key === "ArrowDown") {
        return currentIndex < 0 ? 0 : (currentIndex + 1) % resultCount;
      }
      return currentIndex < 0 ? resultCount - 1 : (currentIndex - 1 + resultCount) % resultCount;
    });
  };

  return createPortal(
    <div className="new-hrm-modal-portal new-hrm-app--premium">
      <div
        className="new-hrm-command-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <section
          className="new-hrm-command-palette"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-hrm-command-title"
        >
          <header className="new-hrm-command-palette__search">
            <FiSearch aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search pages and actions"
              aria-label="Search pages and actions"
              aria-activedescendant={activeIndex >= 0 ? `new-hrm-command-result-${activeIndex}` : undefined}
            />
            <button type="button" onClick={onClose} aria-label="Close search">
              <FiX aria-hidden="true" />
            </button>
          </header>
          <div className="new-hrm-command-palette__body">
            <h2 id="new-hrm-command-title">Pages</h2>
            <div className="new-hrm-command-palette__list">
              {filteredDestinations.map((item, resultIndex) => {
                const Icon = item.icon;
                const NavElement = item.href ? "a" : "button";
                const active = resultIndex === activeIndex;
                return (
                  <NavElement
                    key={item.id}
                    id={`new-hrm-command-result-${resultIndex}`}
                    ref={(node) => { resultRefs.current[resultIndex] = node; }}
                    className={active ? "new-hrm-command-palette__result--active" : undefined}
                    type={item.href ? undefined : "button"}
                    href={item.href}
                    target={item.href ? "_blank" : undefined}
                    rel={item.href ? "noopener noreferrer" : undefined}
                    title={item.href ? `${item.label} (opens in a new tab)` : undefined}
                    onClick={() => item.href ? onClose() : selectDestination(item.id)}
                  >
                    <span className="new-hrm-command-palette__icon"><Icon aria-hidden="true" /></span>
                    <span><strong>{item.label}</strong><small>Open page</small></span>
                    <FiArrowRight aria-hidden="true" />
                  </NavElement>
                );
              })}
            </div>
            {filteredActions.length > 0 && (
              <>
                <h2>Quick actions</h2>
                <div className="new-hrm-command-palette__list">
                  {filteredActions.map((item, actionIndex) => {
                    const Icon = item.icon;
                    const resultIndex = filteredDestinations.length + actionIndex;
                    const active = resultIndex === activeIndex;
                    return (
                      <button
                        key={item.id}
                        id={`new-hrm-command-result-${resultIndex}`}
                        ref={(node) => { resultRefs.current[resultIndex] = node; }}
                        className={active ? "new-hrm-command-palette__result--active" : undefined}
                        type="button"
                        onClick={() => selectAction(item)}
                      >
                        <span className="new-hrm-command-palette__icon"><Icon aria-hidden="true" /></span>
                        <span><strong>{item.label}</strong><small>Start action</small></span>
                        <FiArrowRight aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {!filteredDestinations.length && !filteredActions.length && (
              <p className="new-hrm-command-palette__empty">No matching page or action.</p>
            )}
          </div>
          <footer className="new-hrm-command-palette__footer">
            <span>Type to filter</span>
            <span><kbd>Esc</kbd> Close</span>
          </footer>
        </section>
      </div>
    </div>,
    document.body
  );
};

export default HrmCommandPalette;
